import React, { useCallback, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { FieldLabel } from '@puckeditor/core'
import { Image, Images, Loader2, Trash2, Upload, X } from 'lucide-react'
import {
  deleteMediaFile,
  measureImageFile,
  uploadMediaFile,
  type MediaImage,
} from '../../../services/mediaLibrary'
import { MediaLibraryPicker } from './MediaLibraryPicker'
import * as styles from './FileUploadField.module.scss'

/** The API `Image` (from an upload or the library), or a plain URL. */
export type UploadedFile = MediaImage | string

export type FileUploadFieldType = {
  type: 'fileUpload'
  label: string
  /** MIME types or extensions, e.g. { 'image/*': ['.png', '.jpg'] } */
  accept?: Record<string, string[] | readonly string[]>
  /** Max file size in bytes */
  maxSize?: number
  /** Path to the file in the bucket */
  path?: string
  /**
   * Offer "Choose from library". Defaults to on when the field accepts images: the
   * library grid previews images, and fonts or other files are better uploaded.
   */
  library?: boolean
}

function getFileName(file: UploadedFile): string {
  if (typeof file === 'string') return file
  return file.filename
}

export function getPuckFileUrl(file: UploadedFile): string {
  if (typeof file === 'string') return file
  return file?.link ?? ''
}

// A file can come from the library, so other pages and steps may use it too.
export const DELETE_CONFIRM_MESSAGE =
  'Delete this file from storage? Any page or step that uses it will lose it, ' +
  'and this cannot be undone. Use Clear to remove it from this field only.'

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error && err.message ? err.message : fallback

/**
 * Deletes the file from storage after confirming, then calls `onDeleted`. When the
 * delete fails the field keeps its value (the file is still there) and `onError` gets
 * the reason; without one, the reason is shown in an alert. Resolves whether the file
 * was deleted.
 */
export async function deleteUploadedFile(
  file: UploadedFile,
  onDeleted: () => void,
  onError: (message: string) => void = message => window.alert(message),
): Promise<boolean> {
  if (typeof file === 'string') {
    onDeleted()
    return true
  }
  const confirmed = window.confirm(DELETE_CONFIRM_MESSAGE)
  if (!confirmed) return false
  try {
    await deleteMediaFile(file.id)
  } catch (err) {
    onError(errorMessage(err, 'Delete failed'))
    return false
  }
  onDeleted()
  return true
}

const DEFAULT_ACCEPT = {
  'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
}
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024 // 5MB
const DEFAULT_PATH = '/puck/'

const acceptsImages = (accept: Record<string, unknown>) =>
  Object.keys(accept).some(type => type.startsWith('image/'))

export type FileUploadInputProps = {
  value: UploadedFile | undefined
  onChange: (value: UploadedFile | undefined) => void
  accept?: Record<string, string[] | readonly string[]>
  maxSize?: number
  path?: string
  ariaLabel?: string
  /** Offer "Choose from library" (see FileUploadFieldType.library). */
  showLibrary?: boolean
}

export const FileUploadInput: React.FC<FileUploadInputProps> = ({
  value,
  onChange,
  accept = DEFAULT_ACCEPT,
  maxSize = DEFAULT_MAX_SIZE,
  path = DEFAULT_PATH,
  ariaLabel = 'Upload file',
  showLibrary = acceptsImages(accept),
}) => {
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isLibraryOpen, setIsLibraryOpen] = useState(false)
  const libraryButtonRef = useRef<HTMLButtonElement>(null)

  const handleUpload = useCallback(
    async (file: File) => {
      setUploadError(null)
      setIsUploading(true)
      try {
        const size = await measureImageFile(file)
        onChange(await uploadMediaFile(file, { path, size }))
      } catch (err) {
        setUploadError(errorMessage(err, 'Upload failed'))
      } finally {
        setIsUploading(false)
      }
    },
    [onChange, path],
  )

  const closeLibrary = useCallback(() => {
    setIsLibraryOpen(false)
    // The trigger unmounts once a file is picked; only refocus it when it is still there.
    requestAnimationFrame(() => libraryButtonRef.current?.focus())
  }, [])

  const handleLibrarySelect = useCallback(
    (image: MediaImage) => {
      setUploadError(null)
      setIsLibraryOpen(false)
      onChange(image)
    },
    [onChange],
  )

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (file) handleUpload(file)
    },
    [handleUpload],
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    maxSize,
    maxFiles: 1,
    noClick: true,
    disabled: !!value || isUploading,
    onDropRejected: fileRejections => {
      const msg = fileRejections[0]?.errors[0]?.message ?? 'File rejected'
      setUploadError(msg)
    },
  })

  const handleClear = useCallback(() => {
    if (!value) return
    onChange(undefined)
  }, [value, onChange])

  const handleDelete = useCallback(() => {
    if (!value) return
    if (typeof value === 'string') {
      onChange(undefined)
      return
    }
    setUploadError(null)
    deleteUploadedFile(value, () => onChange(undefined), setUploadError)
  }, [value, onChange])

  const displayName = value ? getFileName(value) : ''
  const acceptedExtensions = Object.values(accept).flat()

  return (
    <div className={styles.root}>
      {value ? (
        <div className={styles.fileRow}>
          <span className={styles.fileName} title={displayName}>
            {displayName}
          </span>
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            title="Clear field (keeps file in storage)"
          >
            <X size={14} />
            Clear
          </button>
          {typeof value !== 'string' && (
            <button
              type="button"
              onClick={handleDelete}
              className={styles.deleteButton}
              title="Delete file from storage"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`${styles.dropzone} ${
            isDragActive ? styles.dropzoneActive : ''
          } ${isUploading ? styles.dropzoneDisabled : ''}`}
        >
          <input {...getInputProps()} aria-label={ariaLabel} />
          {isUploading ? (
            <>
              <Loader2 size={24} className={styles.spinner} />
              <span>Uploading… Please do not interact</span>
            </>
          ) : (
            <>
              <Upload size={24} className={styles.uploadIcon} />
              <span>
                {isDragActive ? 'Drop the file here' : 'Drop a file or '}
                {!isDragActive && (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()
                      open()
                    }}
                    className={styles.browseButton}
                  >
                    click to browse
                  </button>
                )}
              </span>
              {(maxSize > 0 || Object.keys(accept).length) && (
                <span className={styles.hint}>
                  {maxSize > 0 && `Max ${maxSize / 1024 / 1024} MB`}
                  {acceptedExtensions.length
                    ? ` • ${acceptedExtensions.join(', ')}`
                    : ''}
                </span>
              )}
            </>
          )}
        </div>
      )}
      {!value && showLibrary && (
        <button
          ref={libraryButtonRef}
          type="button"
          onClick={() => setIsLibraryOpen(true)}
          className={styles.libraryButton}
          disabled={isUploading}
          aria-haspopup="dialog"
        >
          <Images size={14} />
          Choose from library
        </button>
      )}
      {uploadError && (
        <p className={styles.error} role="alert">
          {uploadError}
        </p>
      )}
      {isLibraryOpen && (
        <MediaLibraryPicker
          onSelect={handleLibrarySelect}
          onClose={closeLibrary}
        />
      )}
    </div>
  )
}

export const FileUploadField = ({
  field,
  value: rawValue,
  onChange,
}: {
  field: FileUploadFieldType
  value: unknown
  onChange: (value: UploadedFile | undefined) => void
}) => {
  const value = rawValue as UploadedFile | undefined
  const accept = field.accept ?? DEFAULT_ACCEPT
  const maxSize = field.maxSize ?? DEFAULT_MAX_SIZE
  const path = field.path ?? DEFAULT_PATH
  const showLibrary = field.library ?? acceptsImages(accept)

  return (
    <FieldLabel label={field.label || 'File'} icon={<Image size={16} />}>
      <FileUploadInput
        value={value}
        onChange={onChange}
        accept={accept}
        maxSize={maxSize}
        path={path}
        ariaLabel={field.label || 'Upload file'}
        showLibrary={showLibrary}
      />
    </FieldLabel>
  )
}
